import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

const STORAGE_KEY = "roulette-board-history-v1";
const VISIBLE_HISTORY_LIMIT = 120;

const redNumbers = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);

type RouletteLane = "red" | "green" | "black";

function isRouletteNumber(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0 && Number(value) <= 36;
}

function readStoredHistory() {
  try {
    const rawHistory = window.localStorage.getItem(STORAGE_KEY);
    const parsed = rawHistory ? JSON.parse(rawHistory) : [];

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isRouletteNumber);
  } catch {
    return [];
  }
}

function getLane(value: number): RouletteLane {
  if (value === 0) {
    return "green";
  }

  return redNumbers.has(value) ? "red" : "black";
}

function parseBufferedNumber(value: string) {
  if (value.length === 0) {
    return null;
  }

  const parsed = Number(value);
  return isRouletteNumber(parsed) ? parsed : null;
}

function getVisibleRowLimit(historyElement: HTMLElement) {
  const rowElement = historyElement.querySelector<HTMLElement>(".spin-row");

  if (!rowElement) {
    return VISIBLE_HISTORY_LIMIT;
  }

  const rowHeight = rowElement.getBoundingClientRect().height;
  const historyHeight = historyElement.getBoundingClientRect().height;
  const rowGap = Number.parseFloat(window.getComputedStyle(historyElement).rowGap);
  const safeRowGap = Number.isFinite(rowGap) ? rowGap : 0;

  if (rowHeight <= 0 || historyHeight <= 0) {
    return VISIBLE_HISTORY_LIMIT;
  }

  const visibleRows = Math.floor((historyHeight + safeRowGap) / (rowHeight + safeRowGap));
  return Math.max(1, Math.min(VISIBLE_HISTORY_LIMIT, visibleRows));
}

function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

function App() {
  const [history, setHistory] = useState<number[]>(readStoredHistory);
  const [visibleRowLimit, setVisibleRowLimit] = useState(VISIBLE_HISTORY_LIMIT);
  const historyElement = useRef<HTMLDivElement>(null);
  const inputBuffer = useRef("");

  const visibleHistory = useMemo(() => {
    return history.slice(-visibleRowLimit);
  }, [history, visibleRowLimit]);
  const visibleHistoryStartIndex = history.length - visibleHistory.length;

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  }, [history]);

  useLayoutEffect(() => {
    const element = historyElement.current;

    if (!element) {
      return;
    }

    const updateVisibleRowLimit = () => {
      const nextVisibleRowLimit = getVisibleRowLimit(element);
      setVisibleRowLimit((currentVisibleRowLimit) => {
        return currentVisibleRowLimit === nextVisibleRowLimit
          ? currentVisibleRowLimit
          : nextVisibleRowLimit;
      });
    };

    updateVisibleRowLimit();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateVisibleRowLimit);
      return () => window.removeEventListener("resize", updateVisibleRowLimit);
    }

    const resizeObserver = new ResizeObserver(updateVisibleRowLimit);
    resizeObserver.observe(element);

    const rowElement = element.querySelector(".spin-row");

    if (rowElement) {
      resizeObserver.observe(rowElement);
    }

    return () => resizeObserver.disconnect();
  }, [history.length]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      if (/^\d$/.test(event.key)) {
        event.preventDefault();

        if (inputBuffer.current.length < 2) {
          inputBuffer.current += event.key;
        }

        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();

        const value = parseBufferedNumber(inputBuffer.current);
        inputBuffer.current = "";

        if (value !== null) {
          setHistory((currentHistory) => [...currentHistory, value]);
        }

        return;
      }

      if (event.key === "Backspace") {
        event.preventDefault();

        if (inputBuffer.current.length > 0) {
          inputBuffer.current = "";
          return;
        }

        setHistory((currentHistory) => currentHistory.slice(0, -1));
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        inputBuffer.current = "";
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <main className="app">
      <section className="board-shell" aria-label="История выпавших номеров рулетки">
        <div className="board">
          <div className="history" aria-live="polite" ref={historyElement}>
            {visibleHistory.map((value, index) => {
              const lane = getLane(value);
              const isLatest = index === visibleHistory.length - 1;
              const historyIndex = visibleHistoryStartIndex + index;

              return (
                <div className={`spin-row ${isLatest ? "is-latest" : ""}`} key={`${historyIndex}-${value}`}>
                  <div className="lane lane-red">
                    {lane === "red" ? (
                      <span className="number number-red">{value}</span>
                    ) : null}
                  </div>
                  <div className="lane lane-green">
                    {lane === "green" ? (
                      <span className="number number-green">{value}</span>
                    ) : null}
                  </div>
                  <div className="lane lane-black">
                    {lane === "black" ? (
                      <span className="number number-black">{value}</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}

registerServiceWorker();

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element was not found");
}

createRoot(rootElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
